import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { goBooking } from "../../lib/promo";
import CaptionedImage from "../CaptionedImage";
import { useLang, pick } from "../../lib/i18n";

export default function Dining({ items, bookingUrl }) {
  const { t, lang } = useLang();
  if (!items?.length) return null;

  return (
    <section id="dining" data-testid="dining-section" className="py-24 sm:py-32 bg-[#FBF9F5]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.25em] font-sans font-medium text-[#9A7B66] mb-5"
        >
          {t("dining.eyebrow")}
        </motion.p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl"
          >
            {t("dining.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-sm text-sm text-[#A0988E] font-sans leading-relaxed"
          >
            {t("dining.intro")}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {items.map((item, i) => {
            const target = item.booking_url || bookingUrl;
            return (
            <motion.article
              key={item.id || i}
              data-testid={`dining-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12 }}
              className="group"
            >
              <div className="overflow-hidden rounded-md h-[300px] sm:h-[400px]">
                <CaptionedImage
                  testId={`dining-image-${i}`}
                  src={mediaUrl(item.image_url)}
                  alt={item.image_alt || pick(item, "title", lang)}
                  title={item.image_title || pick(item, "title", lang)}
                  description={item.image_description || pick(item, "description", lang)}
                  caption={item.image_caption || ""}
                  focalPoint={item.image_focal_point || "50% 0%"}
                  className="w-full h-full"
                  imageClassName="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105"
                />
              </div>
              <div className="pt-6 border-t border-transparent">
                <h3 className="font-serif font-normal text-2xl text-[#2C2825] leading-snug">{pick(item, "title", lang)}</h3>
                {item.note && (
                  <p className="mt-1.5 text-[11px] uppercase tracking-[0.2em] text-[#9A7B66] font-sans font-medium">{item.note}</p>
                )}
                <p className="mt-3 text-sm text-[#6E675F] font-sans leading-relaxed max-w-lg">{pick(item, "description", lang)}</p>
                {target && (
                  <button
                    type="button"
                    data-testid={`dining-booking-link-${i}`}
                    onClick={() => goBooking(target)}
                    className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-sm border border-[#9A7B66] text-[#9A7B66] text-[11px] font-sans font-semibold uppercase tracking-[0.14em] hover:bg-[#9A7B66] hover:text-white transition-colors cursor-pointer"
                  >
                    {t("dining.reserve")} <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
